"use client";

import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-6">
        <div className="h-4 w-20 bg-muted animate-pulse" />
        <div className="h-4 w-20 bg-muted animate-pulse" />
        <div className="h-4 w-20 bg-muted animate-pulse" />
        <div className="h-4 w-20 bg-muted animate-pulse" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-2">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-[#245D6C] font-default uppercase text-sm tracking-widest font-semibold px-3 py-2 relative group bg-transparent hover:bg-transparent focus:bg-transparent">
                <span className="relative inline-block">
                  {item.label}
                  <span className="absolute left-0 -bottom-1 h-[1px] w-0 bg-[#5EB1E9] transition-all duration-200 ease-out group-hover:w-full" />
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 mt-12 px-8">
        <div className="h-10 w-3/4 bg-muted animate-pulse" />
        <div className="h-10 w-2/3 bg-muted animate-pulse" />
        <div className="h-10 w-3/4 bg-muted animate-pulse" />
      </div>;
  }
  return <nav className="flex flex-col gap-8 mt-16 px-8">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} onClick={onNavigate} className="text-[#245D6C] font-default uppercase text-3xl sm:text-4xl tracking-tighter font-bold hover:text-[#5EB1E9] transition-colors duration-200">
          {item.label}
        </Link>)}
    </nav>;
}
export default function MainNavigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="2656" id="main-navigation" className={`sticky top-0 z-50 w-full bg-white text-foreground transition-all duration-300 ${scrolled ? "bg-white/90 backdrop-blur-md border-b border-border shadow-md" : "border-b border-transparent"}`}>
      <div className="w-full flex items-center justify-between py-6 px-6 md:px-12">
        {}
        <div className="flex items-center">
          <Link to="/" aria-label="IMI Elite Management home" className="inline-flex items-center">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660969/img/logo_high_res_transparent.png" className="h-16 w-auto" alt="IMI Elite Management" loading="lazy" />
          </Link>
        </div>

        {}
        <div className="hidden lg:flex items-center justify-center flex-1">
          <MenuProvider menu_id="4">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="hidden lg:flex items-center">
          <Button asChild className="bg-primary border border-primary text-primary-foreground uppercase tracking-widest font-default font-bold text-sm px-7 py-3.5 h-auto rounded-md shadow-md hover:bg-secondary hover:text-secondary-foreground hover:border-secondary transition-all duration-300">
            <Link to="/PartnersPage">Referrals</Link>
          </Button>
        </div>

        {}
        <div className="lg:hidden flex items-center gap-3">
          <Button asChild size="sm" className="bg-primary border border-primary text-primary-foreground uppercase tracking-widest font-default font-bold text-xs px-4 py-2 h-auto rounded-md shadow-sm hover:bg-secondary hover:text-secondary-foreground transition-all duration-300 hidden sm:inline-flex">
            <Link to="/ContactPage">Contact</Link>
          </Button>

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open menu" className="text-foreground hover:bg-transparent hover:text-[#5EB1E9]">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-background text-foreground border-l border-border w-full sm:max-w-md p-0">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <MenuProvider menu_id="4">
                <MobileMenuConsumer onNavigate={() => setMobileOpen(false)} />
              </MenuProvider>
              <div className="mt-12 px-8">
                <Button asChild className="bg-primary border border-primary text-primary-foreground uppercase tracking-widest font-default font-bold text-sm px-7 py-3.5 h-auto w-full rounded-md shadow-md hover:bg-secondary hover:text-secondary-foreground transition-all duration-300">
                  <Link to="/PartnersPage" onClick={() => setMobileOpen(false)}>
                    Referrals
                  </Link>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </section>;
}